import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, TriangleAlert } from "lucide-react";
import { UpdateOrganizationRequestSchema, type OrganizationIdentity } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/notify";
import { useUpdateOrganization } from "../hooks";
import { TrackRecordPolicyCard } from "./TrackRecordPolicyCard";
import { OeeThresholdsCard } from "./OeeThresholdsCard";
import { PlanAdherenceCard } from "./PlanAdherenceCard";
import { SettlementGraceCard } from "./SettlementGraceCard";
import { OeeConfidenceCard } from "./OeeConfidenceCard";
import type { PolicyFormValues } from "./policy-card.types";

// Spec 079 (US4) — A POLÍTICA DE JULGAMENTO DA FÁBRICA, editável sem deploy.
//
// Dez números que decidem o que a plataforma AFIRMA: quando o histórico do gêmeo vale, quando o
// OEE é verde, quando uma produção está "no plano", quando a apuração libera, quando a confiança do
// OEE cai. Três nasceram com esta spec; sete foram RESGATADOS — cinco de constantes de módulo e um
// de variável de ambiente replicada em dois serviços.
//
// ⚠️ UM FORMULÁRIO, UM SALVAR. Os cinco cards são agrupamento VISUAL, não cinco mutations. Quatro
// dos dez campos formam PARES ORDENADOS (alta > média, verde > amarelo, teto > piso, fatia baixa >
// fatia média), e salvar em partes permitiria um estado intermediário com um par invertido — que
// não produz erro nenhum, produz uma FAIXA VAZIA no meio: nenhuma acurácia voltaria a ser "Média",
// nenhum OEE voltaria a ser amarelo, e nada na tela diria por quê.
//
// ⚠️ A validação dos pares é a MESMA do servidor (`UpdateOrganizationRequestSchema`), via
// `zodResolver`. Não é conveniência de UX: uma segunda regra escrita aqui divergiria da rota, e a
// divergência apareceria como um formulário que aceita o que a API recusa com 400.

interface Props {
  organization: OrganizationIdentity;
  canEdit: boolean;
}

/** Os dez valores VIGENTES como ponto de partida do formulário. */
function currentValues(o: OrganizationIdentity): PolicyFormValues {
  return {
    trackRecordMinSample: o.trackRecordMinSample,
    trackRecordAccuracyHigh: o.trackRecordAccuracyHigh,
    trackRecordAccuracyMedium: o.trackRecordAccuracyMedium,
    oeeThresholdGood: o.oeeThresholdGood,
    oeeThresholdWarn: o.oeeThresholdWarn,
    planAdherenceLow: o.planAdherenceLow,
    planAdherenceHigh: o.planAdherenceHigh,
    settlementGraceHours: o.settlementGraceHours,
    oeeConfidenceNodataHigh: o.oeeConfidenceNodataHigh,
    oeeConfidenceNodataLow: o.oeeConfidenceNodataLow,
  };
}

export function PoliciesSection({ organization, canEdit }: Props) {
  const update = useUpdateOrganization();
  const form = useForm<PolicyFormValues>({
    resolver: zodResolver(UpdateOrganizationRequestSchema),
    // ⚠️ Semeado com os valores VIGENTES, e não com `undefined`. A regra de campo numérico vazio
    // vale para CRIAÇÃO (onde um `0` literal obriga a apagar antes de digitar); aqui o gestor está
    // editando uma política que já existe, e um formulário em branco o faria redigitar dez números
    // para mudar um — ou salvar nulos por cima do que estava certo.
    defaultValues: currentValues(organization),
  });

  const disabled = !canEdit || update.isPending;

  async function onSubmit(values: PolicyFormValues) {
    // ⚠️ SÓ OS CAMPOS SUJOS. O `PATCH` aceita parcial, e mandar os dez sempre transformava um
    // formulário aberto há minutos numa sobrescrita: outro gestor muda a carência, esta tela ainda
    // exibe o valor antigo (o `defaultValues` do react-hook-form é lido só na montagem), e salvar
    // um limiar qualquer devolvia a carência dele ao valor velho — sem conflito, sem aviso, e com
    // uma entrada de auditoria que parece intencional.
    const dirty = Object.keys(form.formState.dirtyFields) as Array<keyof PolicyFormValues>;
    if (dirty.length === 0) return;
    const changed = Object.fromEntries(dirty.map((k) => [k, values[k]]));

    // ⚠️ SEM `catch` (guardrail #12): o `MutationCache` global já traduz a falha por
    // `getFriendlyErrorMessage`. Um `toast.error` aqui, sem `meta: { skipErrorToast: true }` no
    // hook, mostrava DOIS toasts com a mesma descrição — e o hook não pode ter o opt-out, porque a
    // seção de identificação, que compartilha a mutation, depende do toast global.
    const saved = await update.mutateAsync(changed);
    // `reset` com o que o SERVIDOR devolveu, não com o que foi enviado: os limiares são
    // `Decimal(5,2)`, então 85.567 é gravado como 85.57 — reexibir o valor enviado deixaria a tela
    // mostrando um número que o banco não tem, com `isDirty` falso e o botão desabilitado.
    form.reset(currentValues(saved));
    toast.success("Política de julgamento salva.");
  }

  const cardProps = { register: form.register, errors: form.formState.errors, disabled };

  return (
    <form className="space-y-4" onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} noValidate>
      <div className="bg-bg-muted-rd/40 flex items-start gap-2 rounded-md border border-border-subtle px-3 py-2">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-rd-warning-600" aria-hidden />
        {/* FR-034 — o aviso que evita o chamado de bug. Sem ele, a régua da tela discordando do
            chip de uma simulação antiga parece defeito, e não a decisão que foi tomada. */}
        <p className="text-[12px] leading-tight text-text-secondary">
          Mudar um destes valores muda o que a plataforma afirma <strong>de agora em diante</strong>
          . Simulações já executadas mantêm a confiança que foi registrada no momento em que rodaram
          — o histórico não é reescrito.
        </p>
      </div>

      <TrackRecordPolicyCard {...cardProps} />
      <OeeThresholdsCard {...cardProps} />
      <PlanAdherenceCard {...cardProps} />
      <SettlementGraceCard {...cardProps} />
      <OeeConfidenceCard {...cardProps} />

      {canEdit ? (
        // "Salvar política", não "Salvar": a tela tem outros botões de salvar (identificação,
        // expediente), e três controles chamados "Salvar" na mesma página são ambíguos —
        // principalmente para quem navega por lista de botões.
        <Button type="submit" disabled={disabled || !form.formState.isDirty}>
          <Save className="mr-1.5 h-4 w-4" aria-hidden /> Salvar política
        </Button>
      ) : (
        <p className="text-[12px] text-text-secondary">
          Apenas gestores podem alterar a política de julgamento da fábrica.
        </p>
      )}
    </form>
  );
}
